/**
 * Stages 10–12 — Professional extension after Course Certificate (1–9).
 * Kind-faithful classes: repertoire stays repertoire, technique stays technique.
 */

import type {
  AssessmentMode,
  ClassKind,
  ClassSegment,
  TutorClass,
  TutorStage,
} from "./types.js";
import { assessmentForKind } from "./kindAssessment.js";

type ClassSeed = {
  stage: 10 | 11 | 12;
  number: number;
  title: string;
  kind: ClassKind;
  minutes?: number;
  description: string;
  skills: string[];
  assessment?: AssessmentMode;
  segments: ClassSegment[];
};

function cls(s: ClassSeed): TutorClass {
  const d = s.minutes ?? 45;
  return {
    id: `class-${s.stage}-${s.number}`,
    stage: s.stage,
    code: `${s.stage}.${s.number}`,
    title: s.title,
    kind: s.kind,
    defaultMinutes: d,
    minMinutes: Math.max(20, Math.round(d * 0.5)),
    maxMinutes: Math.round(d * 1.4),
    description: s.description,
    skills: s.skills,
    assessment: s.assessment ?? assessmentForKind(s.kind),
    segments: s.segments,
  };
}

const STAGE_10: TutorClass[] = [
  cls({
    stage: 10,
    number: 1,
    title: "Two-Voice Invention Track",
    kind: "repertoire",
    minutes: 45,
    description: "Independent voices — subject vs countersubject muscle memory.",
    skills: ["counterpoint", "independence", "memory"],
    segments: [
      { pieceId: "pro-baroque-invention-study", baseLoops: 6, label: "Invention study", focus: "articulation" },
      { pieceId: "pro-fugue-subject-drill", baseLoops: 4, label: "Subject memory", focus: "memory" },
    ],
  }),
  cls({
    stage: 10,
    number: 2,
    title: "Classical Sonatina Exposition",
    kind: "repertoire",
    minutes: 50,
    description: "Theme + Alberti bass — classical style without diluting into walks.",
    skills: ["alberti", "form", "style"],
    segments: [
      { pieceId: "pro-classical-sonatina-a", baseLoops: 7, label: "Sonatina exposition", focus: "timing" },
    ],
  }),
  cls({
    stage: 10,
    number: 3,
    title: "Romantic Lyric Pedal Study",
    kind: "pedal",
    minutes: 45,
    description: "Cantabile line with clean pedal changes.",
    skills: ["pedal", "cantabile", "balance"],
    segments: [
      { pieceId: "pro-romantic-nocturne-study", baseLoops: 6, label: "Lyric study", focus: "pedal" },
    ],
  }),
  cls({
    stage: 10,
    number: 4,
    title: "Prelude Endurance Pattern",
    kind: "repertoire",
    minutes: 40,
    description: "Continuous figuration — stamina without pitch-walk dilution.",
    skills: ["endurance", "evenness", "pattern"],
    segments: [
      { pieceId: "pro-prelude-pattern-study", baseLoops: 8, metronomeLocked: true, bpm: 92, label: "Prelude pattern", focus: "timing" },
    ],
  }),
  cls({
    stage: 10,
    number: 5,
    title: "Repertoire Memory Seal",
    kind: "blind",
    minutes: 40,
    description: "Play invention & fugue subject with no green key guides.",
    skills: ["memory", "blind play", "security"],
    segments: [
      { pieceId: "pro-fugue-subject-drill", baseLoops: 5, label: "Blind subject", focus: "memory" },
      { pieceId: "pro-baroque-invention-study", baseLoops: 4, label: "Blind invention", focus: "memory" },
    ],
  }),
  cls({
    stage: 10,
    number: 6,
    title: "Style Contrast Marathon",
    kind: "performance",
    minutes: 55,
    description: "Baroque → Classical → Romantic takes back-to-back.",
    skills: ["style", "performance", "continuity"],
    assessment: "recital",
    segments: [
      { pieceId: "pro-baroque-invention-study", baseLoops: 3, label: "Baroque take", focus: "articulation" },
      { pieceId: "pro-classical-sonatina-a", baseLoops: 3, label: "Classical take", focus: "timing" },
      { pieceId: "pro-romantic-nocturne-study", baseLoops: 3, label: "Romantic take", focus: "dynamics" },
    ],
  }),
];

const STAGE_11: TutorClass[] = [
  cls({
    stage: 11,
    number: 1,
    title: "Octave Control Lab",
    kind: "speed",
    minutes: 40,
    description: "Power with control — octave outlines only.",
    skills: ["octaves", "control", "strength"],
    segments: [
      { pieceId: "pro-octave-etude", baseLoops: 8, metronomeLocked: true, bpm: 80, label: "Octave etude", focus: "articulation" },
    ],
  }),
  cls({
    stage: 11,
    number: 2,
    title: "Double Thirds Circuit",
    kind: "drill",
    minutes: 40,
    description: "Diatonic thirds for finger independence.",
    skills: ["thirds", "evenness", "finger independence"],
    segments: [
      { pieceId: "pro-double-thirds-c", baseLoops: 8, metronomeLocked: true, bpm: 72, label: "Double thirds", focus: "timing" },
    ],
  }),
  cls({
    stage: 11,
    number: 3,
    title: "All-Key Scale Bursts",
    kind: "scale",
    minutes: 45,
    description: "Sharp-key major circuit at performance tempo.",
    skills: ["scales", "keys", "tempo"],
    segments: [
      { pieceId: "pro-all-key-scale-burst", baseLoops: 6, metronomeLocked: true, bpm: 96, label: "Scale bursts", focus: "timing" },
    ],
  }),
  cls({
    stage: 11,
    number: 4,
    title: "Arpeggio Storm",
    kind: "speed",
    minutes: 40,
    description: "Wide arpeggios for virtuoso agility.",
    skills: ["arpeggios", "agility", "accuracy"],
    segments: [
      { pieceId: "pro-arp-storm", baseLoops: 7, metronomeLocked: true, bpm: 100, label: "Arp storm", focus: "timing" },
    ],
  }),
  cls({
    stage: 11,
    number: 5,
    title: "Ornament Engine",
    kind: "drill",
    minutes: 35,
    description: "Trills and mordents into clean resolutions.",
    skills: ["ornaments", "trill", "control"],
    segments: [
      { pieceId: "pro-trill-ornament-prep", baseLoops: 8, label: "Trill prep", focus: "articulation" },
    ],
  }),
  cls({
    stage: 11,
    number: 6,
    title: "Voicing & Balance",
    kind: "independence",
    minutes: 40,
    description: "Melody projects; accompaniment stays soft.",
    skills: ["voicing", "balance", "dynamics"],
    segments: [
      { pieceId: "pro-voicing-balance", baseLoops: 7, label: "Balance study", focus: "dynamics" },
    ],
  }),
];

const STAGE_12: TutorClass[] = [
  cls({
    stage: 12,
    number: 1,
    title: "Mock Jury — Work A",
    kind: "performance",
    minutes: 50,
    description: "Uninterrupted jury take — pitch, timing, recovery.",
    skills: ["jury", "continuity", "nerves"],
    assessment: "recital",
    segments: [
      { pieceId: "pro-mock-jury-a", baseLoops: 5, label: "Jury A", focus: "articulation" },
    ],
  }),
  cls({
    stage: 12,
    number: 2,
    title: "Mock Jury — Work B",
    kind: "performance",
    minutes: 50,
    description: "Contrasting lyrical jury work.",
    skills: ["jury", "lyricism", "pedal"],
    assessment: "recital",
    segments: [
      { pieceId: "pro-mock-jury-b", baseLoops: 5, label: "Jury B", focus: "dynamics" },
    ],
  }),
  cls({
    stage: 12,
    number: 3,
    title: "Professional Sight Line",
    kind: "sight",
    minutes: 40,
    description: "Advanced unseen reading — staff first, no hand-holding walks.",
    skills: ["sight-reading", "chromatic", "tempo"],
    segments: [
      { pieceId: "pro-sight-open-score", baseLoops: 8, label: "Sight line", focus: "pitch" },
    ],
  }),
  cls({
    stage: 12,
    number: 4,
    title: "Transposition Lab",
    kind: "theory",
    minutes: 40,
    description: "Same motif moved by step — mental flexibility.",
    skills: ["transposition", "theory", "ear"],
    segments: [
      { pieceId: "pro-transpose-phrase", baseLoops: 7, label: "Transpose phrases", focus: "pitch" },
    ],
  }),
  cls({
    stage: 12,
    number: 5,
    title: "Cadenza Freedom",
    kind: "improvisation",
    minutes: 35,
    description: "Flourish into a firm cadence — controlled freedom.",
    skills: ["cadenza", "rubato feel", "cadence"],
    segments: [
      { pieceId: "pro-cadenza-freedom", baseLoops: 6, label: "Cadenza study", focus: "articulation" },
    ],
  }),
  cls({
    stage: 12,
    number: 6,
    title: "Professional Diploma Recital",
    kind: "performance",
    minutes: 60,
    description: "Final pro-track capstone — full uninterrupted take.",
    skills: ["recital", "recording", "professional"],
    assessment: "recital",
    segments: [
      { pieceId: "pro-mock-jury-a", baseLoops: 2, label: "Warm jury A", focus: "timing" },
      { pieceId: "pro-final-diploma-recital", baseLoops: 5, bpm: 90, metronomeLocked: true, label: "Diploma capstone", focus: "dynamics" },
    ],
  }),
];

export const PRO_STAGES: TutorStage[] = [
  {
    id: 10,
    title: "Repertoire Track",
    blurb: "Professional extension — invention, sonatina, lyric & prelude studies (~4.5 hrs).",
    examId: "exam-stage-10",
    classes: STAGE_10,
  },
  {
    id: 11,
    title: "Virtuoso Technique",
    blurb: "Octaves, thirds, all-key bursts, ornaments, voicing (~4 hrs).",
    examId: "exam-stage-11",
    classes: STAGE_11,
  },
  {
    id: 12,
    title: "Professional Performance Prep",
    blurb: "Mock jury, advanced sight, transposition, diploma recital (~4.5 hrs).",
    examId: "exam-stage-12",
    classes: STAGE_12,
  },
];
